import type { AgentHealthPoint } from '../api/devices'

/**
 * Cele doua linii AmCharts de pe Health (Historic CPU / Memory): Agent si
 * Service, fill 0.08, inaltime 180. Fara AmCharts - portalul il trage din
 * jQuery. Gol: aceeasi propozitie "No CPU samples in the last 24h."
 */

const W = 640
const H = 160
const PAD = { top: 12, right: 12, bottom: 24, left: 48 }

export function AgentMonitorChart({
  what,
  unit,
  range,
  points,
  memory,
}: {
  what: 'CPU' | 'memory'
  unit: string
  range: string
  points: AgentHealthPoint[]
  memory?: boolean
}) {
  if (points.length === 0) {
    const when = ['1h', '24h', '7d', '30d'].includes(range) ? `in the last ${range}` : 'for this day'
    return (
      <div className="alert alert-info" style={{ margin: 0 }}>
        <i className="fas fa-info-circle"></i> No {what} samples {when}.
      </div>
    )
  }

  const agentKey = memory ? 'optimumdesk_memory' : 'optimumdesk_cpu'
  const serviceKey = memory ? 'od_service_memory' : 'od_service_cpu'
  const xs = points.map((_, i) => i)
  const ys = points.flatMap((p) => [num(p[agentKey]), num(p[serviceKey])]).filter((n): n is number => n !== null)
  const max = Math.max(1, ...ys)
  const innerW = W - PAD.left - PAD.right
  const innerH = H - PAD.top - PAD.bottom
  const x = (i: number) => PAD.left + (points.length === 1 ? innerW / 2 : (i / (points.length - 1)) * innerW)
  const y = (v: number) => PAD.top + innerH - (v / max) * innerH
  const path = (key: 'optimumdesk_cpu' | 'optimumdesk_memory' | 'od_service_cpu' | 'od_service_memory') =>
    points
      .map((p, i) => {
        const v = num(p[key]) ?? 0
        return `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(v).toFixed(1)}`
      })
      .join(' ')

  const long = range === '7d' || range === '30d'
  const ticks = tickIndexes(points.length).map((i) => ({
    x: x(i),
    label: labelAt(points[i]!.date, long),
  }))

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={180} role="img" aria-label={`Historic ${what}`}>
        <path d={path(agentKey)} fill="none" stroke="#67b7dc" strokeWidth={2} />
        <path d={path(serviceKey)} fill="none" stroke="#84b761" strokeWidth={2} />
        {xs.map((i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(num(points[i]![agentKey]) ?? 0)} r={3} fill="#67b7dc" />
            <circle cx={x(i)} cy={y(num(points[i]![serviceKey]) ?? 0)} r={3} fill="#84b761" />
          </g>
        ))}
        {ticks.map((t) => (
          <text key={t.x} x={t.x} y={H - 6} textAnchor="middle" fontSize={10} fill="#8a97a8">
            {t.label}
          </text>
        ))}
        <text x={4} y={PAD.top + 4} fontSize={10} fill="#8a97a8">
          {memory ? formatMem(max) : max.toFixed(2) + unit}
        </text>
      </svg>
      <div style={{ fontSize: 11, color: '#3a4a5b', textAlign: 'center', marginTop: 2 }}>
        <span style={{ color: '#67b7dc' }}>●</span> Agent{' '}
        <span style={{ color: '#84b761', marginLeft: 10 }}>●</span> Service
      </div>
    </div>
  )
}

function num(v: number | null | undefined): number | null {
  return v === null || v === undefined || Number.isNaN(Number(v)) ? null : Number(v)
}

function tickIndexes(n: number): number[] {
  if (n <= 1) return [0]
  if (n <= 6) return Array.from({ length: n }, (_, i) => i)
  return [0, Math.floor((n - 1) / 2), n - 1]
}

function labelAt(date: string, long: boolean): string {
  const d = new Date(date.replace(' ', 'T'))
  if (Number.isNaN(d.getTime())) return date
  const hh = String(d.getHours()).padStart(2, '0')
  const mm = String(d.getMinutes()).padStart(2, '0')
  if (!long) return `${hh}:${mm}`
  const mon = d.toLocaleString('en-US', { month: 'short' })
  return `${mon} ${d.getDate()}, ${hh}:${mm}`
}

function formatMem(n: number): string {
  if (n >= 1024) return `${Math.round((n / 1024) * 100) / 100} GB`
  return `${n.toFixed(2)} MB`
}
